import { useState, useCallback } from 'react';

/**
 * Hook to manage all modal states and handlers in MyCloset
 * Extracts modal logic to reduce MyCloset.js file size
 */
function useMyClosetModals() {
  // General error modal states
  const [isGeneralErrorModalOpen, setIsGeneralErrorModalOpen] = useState(false);
  const [generalErrorConfig, setGeneralErrorConfig] = useState({});

  // VTO Login Confirmation Modal state
  const [isVTOLoginModalOpen, setIsVTOLoginModalOpen] = useState(false);

  // Virtual try-on modal states
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('Processing virtual try-on...');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const [currentRetryAction, setCurrentRetryAction] = useState(null);
  const [currentManualAction, setCurrentManualAction] = useState(null);
  const [processingCreditsUsed, setProcessingCreditsUsed] = useState(null);
  const [processingIsCached, setProcessingIsCached] = useState(false);

  // Earnings Modal state
  const [isEarningsModalOpen, setIsEarningsModalOpen] = useState(false);

  // Helper modals
  const [isPersonImageModalOpen, setIsPersonImageModalOpen] = useState(false);
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Bulk delete and add to closet modals
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isAddToClosetModalOpen, setIsAddToClosetModalOpen] = useState(false);

  // Clothing modal
  const [isClothingModalOpen, setIsClothingModalOpen] = useState(false);
  const [editClothingItemId, setEditClothingItemId] = useState(null);

  // General error modal handlers
  const showGeneralErrorModal = useCallback((config) => {
    setGeneralErrorConfig(config);
    setIsGeneralErrorModalOpen(true);
  }, []);

  const hideGeneralErrorModal = useCallback(() => {
    setIsGeneralErrorModalOpen(false);
    setGeneralErrorConfig({});
  }, []);

  // VTO Login modal handlers
  const showVTOLoginModal = useCallback(() => {
    setIsVTOLoginModalOpen(true);
  }, []);

  const hideVTOLoginModal = useCallback(() => {
    setIsVTOLoginModalOpen(false);
  }, []);

  const handleVTOLogin = useCallback(() => {
    hideVTOLoginModal();
    // Reload the page to pick up the new auth state after inline login
    window.location.reload();
  }, [hideVTOLoginModal]);

  // Processing modal handlers
  const showProcessingModal = useCallback((message = 'Processing virtual try-on...', progress = 0, creditsUsed = null, isCached = false) => {
    setProcessingMessage(message);
    setProcessingProgress(progress);
    setProcessingCreditsUsed(creditsUsed);
    setProcessingIsCached(isCached);
    setIsProcessingModalOpen(true);
  }, []);

  const hideProcessingModal = useCallback(() => {
    setIsProcessingModalOpen(false);
    setProcessingProgress(0);
    setProcessingCreditsUsed(null);
    setProcessingIsCached(false);
  }, []);

  // Error modal handlers
  const showErrorModal = useCallback((error, retryAction = null, manualAction = null) => {
    setCurrentError(error);
    setCurrentRetryAction(() => retryAction);
    setCurrentManualAction(() => manualAction);
    setIsErrorModalOpen(true);
    // Hide processing modal if showing
    setIsProcessingModalOpen(false);
    setProcessingProgress(0);
    setProcessingCreditsUsed(null);
    setProcessingIsCached(false);
  }, []);

  const hideErrorModal = useCallback(() => {
    setIsErrorModalOpen(false);
    setCurrentError(null);
    setCurrentRetryAction(null);
    setCurrentManualAction(null);
  }, []);

  const handleErrorRetry = useCallback(() => {
    hideErrorModal();
    if (currentRetryAction) {
      currentRetryAction();
    }
  }, [currentRetryAction, hideErrorModal]);

  const handleErrorSwitchToManual = useCallback(() => {
    hideErrorModal();
    if (currentManualAction) {
      currentManualAction();
    }
  }, [currentManualAction, hideErrorModal]);

  // Person image modal handlers
  const handlePersonImageModalClose = useCallback(() => {
    setIsPersonImageModalOpen(false);
  }, []);

  // Background modal handlers
  const handleBackgroundModalClose = useCallback(() => {
    setIsBackgroundModalOpen(false);
  }, []);

  // Login modal handlers
  const handleLoginModalClose = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const handleLoginRedirect = useCallback(() => {
    setIsLoginModalOpen(false);
    // Reload the page to pick up the new auth state after inline login
    window.location.reload();
  }, []);

  // Clothing modal handlers
  const handleOpenClothingModal = useCallback((itemId = null, droppedFile = null) => {
    setEditClothingItemId(itemId);
    setIsClothingModalOpen(true);
    // Store dropped file for ClothingModal to pick up
    if (droppedFile) {
      window.__pendingClothingFile = droppedFile;
    }
  }, []);

  const handleCloseClothingModal = useCallback(() => {
    setIsClothingModalOpen(false);
    setEditClothingItemId(null);
  }, []);

  return {
    // General error modal
    isGeneralErrorModalOpen,
    generalErrorConfig,
    showGeneralErrorModal,
    hideGeneralErrorModal,

    // VTO Login modal
    isVTOLoginModalOpen,
    showVTOLoginModal,
    hideVTOLoginModal,
    handleVTOLogin,

    // Processing modal
    isProcessingModalOpen,
    processingProgress,
    processingMessage,
    processingCreditsUsed,
    processingIsCached,
    showProcessingModal,
    hideProcessingModal,

    // Error modal
    isErrorModalOpen,
    currentError,
    currentRetryAction,
    currentManualAction,
    showErrorModal,
    hideErrorModal,
    handleErrorRetry,
    handleErrorSwitchToManual,

    // Person image modal
    isEarningsModalOpen,
    setIsEarningsModalOpen,
    isPersonImageModalOpen,
    setIsPersonImageModalOpen,
    handlePersonImageModalClose,

    // Background modal
    isBackgroundModalOpen,
    setIsBackgroundModalOpen,
    handleBackgroundModalClose,

    // Login modal
    isLoginModalOpen,
    setIsLoginModalOpen,
    handleLoginModalClose,
    handleLoginRedirect,

    // Bulk delete modal
    isBulkDeleteModalOpen,
    setIsBulkDeleteModalOpen,

    // Add to closet modal
    isAddToClosetModalOpen,
    setIsAddToClosetModalOpen,

    // Clothing modal
    isClothingModalOpen,
    editClothingItemId,
    handleOpenClothingModal,
    handleCloseClothingModal
  };
}

export default useMyClosetModals;
